import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  SwapHoriz,
  Person,
  Description,
  CheckCircle,
  Wifi,
} from '@mui/icons-material';
import ClientSelect from '../selects/ClientSelect';
import ContractsActiveSelect from '../selects/ContractsActiveSelect';
import ContractServicosSelect from '../selects/ContractServicosSelect';
import { Client, ContratoServicos } from '../../types';
import Swal from 'sweetalert2';

interface TransferEquipamentoModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newContractId: number, newServicoId: number) => void;
  equipamento: {
    id: number;
    modelo?: {
      titulo: string;
      tipo: string;
    };
    numero_serie: string;
    mac_address: string;
    gpon_sn?: string;
  } | null;
  loading?: boolean;
}

const TransferEquipamentoModal: React.FC<TransferEquipamentoModalProps> = ({
  open,
  onClose,
  onConfirm,
  equipamento,
  loading = false,
}) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [selectedServicoId, setSelectedServicoId] = useState<number | null>(null);
  const [selectedServico, setSelectedServico] = useState<ContratoServicos | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Resetar estado quando modal abrir/fechar
  useEffect(() => {
    if (open) {
      setSelectedClient(null);
      setSelectedContractId(null);
      setSelectedServicoId(null);
      setSelectedServico(null);
      setActiveStep(0);
      setError(null);
    }
  }, [open]);

  const handleClientChange = (clientId: number | null, client?: Client) => {
    setSelectedClient(client || null);
    setSelectedContractId(null);
    setSelectedServicoId(null);
    setSelectedServico(null);
    setError(null);

    if (clientId) {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  };

  const handleContractChange = (contractId: number | number[] | null) => {
    const id = Array.isArray(contractId) ? (contractId.length > 0 ? contractId[0] : null) : contractId;
    setSelectedContractId(id);
    setSelectedServicoId(null);
    setSelectedServico(null);
    setError(null);

    if (id) {
      setActiveStep(2);
    } else {
      setActiveStep(1);
    }
  };

  const handleServicoChange = (servicoId: number | null, servico?: ContratoServicos) => {
    setSelectedServicoId(servicoId);
    setSelectedServico(servico || null);
    setError(null);

    if (servicoId) {
      setActiveStep(3);
    } else {
      setActiveStep(2);
    }
  };

  const handleConfirm = () => {
    if (!selectedContractId) {
      setError('Selecione um contrato para transferir o equipamento');
      return;
    }
    if (!selectedServicoId) {
      setError('Selecione um serviço (não cancelado) do contrato de destino');
      return;
    }
    Swal.fire({
      title: 'Confirmação',
      text: `Tem certeza que deseja transferir o equipamento para o contrato ${selectedContractId} (Serviço ${selectedServico?.tipo ? selectedServico.tipo.toUpperCase() : ''} - ID ${selectedServicoId})?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, transferir',
      cancelButtonText: 'Cancelar',
      customClass: {
        container: 'swal2-container-high-zindex'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onConfirm(selectedContractId, selectedServicoId);
      }
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const steps = [
    {
      label: 'Selecionar Cliente',
      description: 'Escolha o cliente que possui o contrato de destino',
      icon: <Person />,
    },
    {
      label: 'Selecionar Contrato',
      description: 'Escolha o contrato ativo de destino',
      icon: <Description />,
    },
    {
      label: 'Selecionar Serviço',
      description: 'Escolha o serviço (não cancelado) do contrato de destino',
      icon: <Wifi />,
    },
    {
      label: 'Confirmar Transferência',
      description: 'Revise os dados e confirme a transferência',
      icon: <CheckCircle />,
    },
  ];

  const getEquipamentoIcon = (tipo?: string) => {
    switch (tipo?.toLowerCase()) {
      case 'roteador':
        return '🛰️';
      case 'onu':
      case 'ont':
        return '📡';
      case 'switch':
        return '🔌';
      default:
        return '🔧';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '520px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SwapHoriz color="primary" />
          <Typography variant="h6" component="div">
            Transferir Equipamento
          </Typography>
        </Box>
        {equipamento && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Transferindo equipamento: <strong>{equipamento.modelo?.titulo || 'N/A'}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Série: {equipamento.numero_serie} | MAC: {equipamento.mac_address}
            </Typography>
            {equipamento.gpon_sn && (
              <Typography variant="caption" color="text.secondary" display="block">
                GPON SN: {equipamento.gpon_sn}
              </Typography>
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  onClick={() => {
                    if (index < activeStep && !loading) {
                      setActiveStep(index);
                    }
                  }}
                  sx={{
                    cursor: index < activeStep && !loading ? 'pointer' : 'default',
                  }}
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: activeStep >= index ? 'primary.main' : 'grey.300',
                        color: activeStep >= index ? 'white' : 'grey.600',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {step.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </StepLabel>
                <StepContent>
                  {index === 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ClientSelect
                        value={selectedClient?.id || null}
                        onChange={handleClientChange}
                        label="Cliente de Destino"
                        placeholder="Digite para buscar um cliente"
                        required
                        helperText="Selecione o cliente que possui o contrato de destino"
                      />
                    </Box>
                  )}

                  {index === 1 && selectedClient && (
                    <Box sx={{ mt: 2 }}>
                      <ContractsActiveSelect
                        value={selectedContractId}
                        onChange={handleContractChange}
                        label="Contrato de Destino"
                        placeholder="Selecione um contrato ativo"
                        required
                        clientId={selectedClient.id}
                        helperText="Selecione o contrato ativo para transferir o equipamento"
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setActiveStep(0)}
                          variant="text"
                        >
                          Voltar para Selecionar Cliente
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {index === 2 && selectedClient && selectedContractId && (
                    <Box sx={{ mt: 2 }}>
                      <ContractServicosSelect
                        value={selectedServicoId}
                        onChange={handleServicoChange}
                        contractId={selectedContractId}
                        label="Serviço de Destino"
                        placeholder="Selecione um serviço não cancelado"
                        required
                        disabled={loading}
                        size="small"
                        onlyActive={true}
                        showContractTitle={false}
                        allowClear={false}
                        error={!!error && !selectedServicoId}
                        helperText={!selectedServicoId ? 'Selecione um serviço (não cancelado) do contrato de destino' : ''}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setActiveStep(1)}
                          variant="text"
                        >
                          Voltar para Selecionar Contrato
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {index === 3 && selectedClient && selectedContractId && selectedServicoId && equipamento && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Resumo da Transferência:</strong>
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h4">
                            {getEquipamentoIcon(equipamento.modelo?.tipo)}
                          </Typography>
                          <Box>
                            <Typography variant="body2">
                              • <strong>Equipamento:</strong> {equipamento.modelo?.titulo || 'N/A'} (ID: {equipamento.id})
                            </Typography>
                            <Typography variant="body2">
                              • <strong>Tipo:</strong> {equipamento.modelo?.tipo || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              • <strong>Série:</strong> {equipamento.numero_serie}
                            </Typography>
                            <Typography variant="body2">
                              • <strong>MAC:</strong> {equipamento.mac_address}
                            </Typography>
                            {equipamento.gpon_sn && (
                              <Typography variant="body2">
                                • <strong>GPON SN:</strong> {equipamento.gpon_sn}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                          <Typography variant="body2">
                            • <strong>Cliente de Destino:</strong> {selectedClient.nome}
                          </Typography>
                          <Typography variant="body2">
                            • <strong>Contrato de Destino:</strong> ID {selectedContractId}
                          </Typography>
                          <Typography variant="body2">
                            • <strong>Serviço de Destino:</strong> {selectedServico?.tipo ? selectedServico.tipo.toUpperCase() : 'N/A'} (ID: {selectedServicoId}) {selectedServico?.plano?.nome ? ` - ${selectedServico.plano.nome}` : ''}
                            {selectedServico?.usuario_pppoe ? ` (${selectedServico.usuario_pppoe})` : ''}
                          </Typography>
                          {selectedServico?.endereco_inst && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Endereço: {selectedServico.endereco_inst}, {selectedServico.numero_inst || 'S/N'} - {selectedServico.bairro_inst || ''} ({selectedServico.cidade_inst}/{selectedServico.estado_inst})
                            </Typography>
                          )}
                        </Box>
                      </Alert>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setActiveStep(2)}
                          variant="text"
                        >
                          Alterar Serviço
                        </Button>
                      </Box>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!selectedContractId || !selectedServicoId || loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SwapHoriz />}
        >
          {loading ? 'Transferindo...' : 'Transferir Equipamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferEquipamentoModal;
